import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Choosing and paying for a plan on the website — docs/32.
 *
 * Everything that matters is decided by the server: the price for where the
 * visitor is, which company sells it, which tax is inside, and which gateway
 * takes the money. This shows it and starts it. India: Razorpay's
 * Checkout.js opens over the page (or Razorpay's own page, if the script
 * cannot load). Everywhere else: the browser goes to Stripe's checkout and
 * comes back to `/plans?checkout=…&result=success`, where this asks the
 * server what became of it.
 *
 * What it must always show before anybody pays: the price, that it includes
 * tax, that it renews until cancelled, who the seller is, and links to the
 * terms and the refund policy. Never in the phone app (Apple's and Google's
 * rules); this is the website's component only.
 */

export interface CheckoutTransport {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
}

interface Price { currency: string; minor: number; display: string }

interface Quote {
  country: string | null;
  indian: boolean;
  gateway: string;
  available: boolean;
  seller: { id: string; name: string; country: string; address: string; email: string | null };
  taxLine: string;
  plans: Array<{ productKey: string; tier: 'plus' | 'family'; period: 'monthly' | 'annual'; price: Price | null; ready: boolean }>;
}

interface StartedCheckout {
  id: string;
  gateway: string;
  razorpay?: {
    keyId: string;
    subscriptionId: string;
    name: string;
    description: string;
    prefill: Record<string, string>;
    shortUrl: string | null;
  };
  stripe?: { url: string };
}

/** The countries a billing address is most often in, then "somewhere else". */
const COUNTRIES: Array<[string, string]> = [
  ['IN', 'India'], ['AE', 'United Arab Emirates'], ['US', 'United States'], ['GB', 'United Kingdom'],
  ['SG', 'Singapore'], ['AU', 'Australia'], ['CA', 'Canada'], ['DE', 'Germany'], ['FR', 'France'],
  ['IE', 'Ireland'], ['NL', 'Netherlands'], ['SA', 'Saudi Arabia'], ['QA', 'Qatar'], ['KW', 'Kuwait'],
  ['OM', 'Oman'], ['BH', 'Bahrain'], ['NZ', 'New Zealand'], ['MY', 'Malaysia'], ['NP', 'Nepal'], ['LK', 'Sri Lanka'],
];

const TIER: Record<string, string> = { plus: 'DrBell Plus', family: 'DrBell Family' };

const RAZORPAY_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

type RazorpayCtor = new (options: Record<string, unknown>) => { open: () => void; on: (e: string, f: (r: unknown) => void) => void };

/** Load Checkout.js once. Rejects when it cannot (a blocker, or a page policy that refuses it). */
function loadRazorpay(): Promise<RazorpayCtor> {
  const w = window as unknown as { Razorpay?: RazorpayCtor };
  if (w.Razorpay) return Promise.resolve(w.Razorpay);
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT;
    script.async = true;
    script.onload = () => (w.Razorpay ? resolve(w.Razorpay) : reject(new Error('no Razorpay')));
    script.onerror = () => reject(new Error('Checkout.js could not load'));
    document.head.appendChild(script);
  });
}

function message(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

/** One press, one key: a second click on the same plan reopens the same checkout. */
function pressKey(productKey: string, country: string): string {
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Math.random()).slice(2);
  return `${productKey}:${country}:${id}`.slice(0, 80);
}

export function Checkout({
  transport,
  signedIn,
  onSignIn,
  onActive,
  legalOrigin = 'https://drbell.app',
}: {
  transport: CheckoutTransport;
  signedIn: boolean;
  /** Called when a signed-out visitor chooses a plan. */
  onSignIn?: () => void;
  /** Called once the new plan is confirmed active. */
  onActive?: () => void;
  /** Where /legal/terms and /legal/refunds live. */
  legalOrigin?: string;
}) {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [country, setCountry] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<'active' | 'pending' | 'cancelled' | null>(null);
  const keys = useRef(new Map<string, string>());

  const load = useCallback(async (declared?: string) => {
    try {
      // Concatenated, not interpolated: check-reachable reads route templates literally.
      const query = declared ? `?country=${encodeURIComponent(declared)}` : '';
      const q = await transport.get<Quote>('/api/billing/web/quote' + query);
      setQuote(q);
      setCountry((c) => c || q.country || '');
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Back from Stripe (or Razorpay's own page): ask what became of it. */
  const follow = useCallback(async (checkoutId: string) => {
    setDone('pending');
    for (let i = 0; i < 10; i++) {
      try {
        const r = await transport.get<{ status: string }>(`/api/billing/web/checkouts/${encodeURIComponent(checkoutId)}`);
        if (r.status === 'completed') {
          setDone('active');
          onActive?.();
          return;
        }
        if (r.status === 'failed' || r.status === 'abandoned') {
          setDone(null);
          setError(r.status === 'failed'
            ? 'That payment could not be accepted here, and anything taken is being refunded. If you live in India, choose India as your billing country.'
            : 'That checkout was not finished. Nothing was charged.');
          return;
        }
      } catch {
        /* keep asking a little longer */
      }
      await new Promise((r) => setTimeout(r, 3000));
    }
    // Still not confirmed: the payment is safe, the webhook or the hourly check will finish it.
  }, [onActive, transport]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('checkout');
    if (!id) return;
    if (params.get('result') === 'cancelled') {
      setDone('cancelled');
      return;
    }
    void follow(id);
  }, [follow]);

  const choose = async (productKey: string) => {
    if (!signedIn) {
      onSignIn?.();
      return;
    }
    setBusy(productKey);
    setError(null);
    try {
      const k = `${productKey}:${country}`;
      if (!keys.current.has(k)) keys.current.set(k, pressKey(productKey, country));
      const { checkout } = await transport.post<{ checkout: StartedCheckout }>('/api/billing/web/checkout', {
        productKey,
        ...(country ? { billingCountry: country } : {}),
        idempotencyKey: keys.current.get(k),
      });
      if (checkout.stripe) {
        window.location.assign(checkout.stripe.url);
        return;
      }
      if (checkout.razorpay) {
        const rz = checkout.razorpay;
        let Razorpay: RazorpayCtor;
        try {
          Razorpay = await loadRazorpay();
        } catch {
          // The page could not load Checkout.js: Razorpay's own page instead.
          if (rz.shortUrl) {
            window.location.assign(rz.shortUrl);
            return;
          }
          throw new Error('The payment window could not open. Check that nothing is blocking checkout.razorpay.com, then try again.');
        }
        const instance = new Razorpay({
          key: rz.keyId,
          subscription_id: rz.subscriptionId,
          name: rz.name,
          description: rz.description,
          prefill: rz.prefill,
          theme: { color: '#0f766e' },
          modal: { ondismiss: () => setBusy(null) },
          handler: async (response: { razorpay_payment_id: string; razorpay_subscription_id: string; razorpay_signature: string }) => {
            setDone('pending');
            try {
              const r = await transport.post<{ status: string }>('/api/billing/web/razorpay/confirm', { checkoutId: checkout.id, ...response });
              if (r.status === 'completed') {
                setDone('active');
                onActive?.();
              }
            } catch {
              // Paid: the webhook finishes it even if this call failed.
            } finally {
              setBusy(null);
            }
          },
        });
        instance.open();
        return;
      }
      throw new Error('The payment could not be started.');
    } catch (e) {
      setError(message(e));
      setBusy(null);
    }
  };

  if (done === 'active') {
    return (
      <div className="rounded-2xl border border-(--line) bg-(--panel) p-5">
        <h2 className="text-lg font-semibold">Your plan is active</h2>
        <p className="mt-2 text-sm text-(--muted)">
          Thank you. It works on every phone you sign in to with this account. Your invoice is below, and Razorpay or
          Stripe also emails a receipt.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {done === 'pending' && (
        <p role="status" className="rounded-lg border border-(--line) bg-(--panel) p-3 text-sm">
          Confirming your payment… This usually takes a few seconds. You can leave this page: your plan starts as soon
          as the payment is confirmed.
        </p>
      )}
      {done === 'cancelled' && (
        <p role="status" className="rounded-lg border border-(--line) bg-(--panel) p-3 text-sm">
          You left the checkout. Nothing was charged.
        </p>
      )}
      {error && (
        <p role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-700">
          {error}
        </p>
      )}

      {quote && (
        <>
          <label className="flex flex-col gap-1 text-sm sm:max-w-xs">
            <span className="text-(--muted)">Country of your billing address</span>
            <select
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                void load(e.target.value);
              }}
              className="rounded-lg border border-(--line) bg-(--bg) px-3 py-2"
            >
              {!COUNTRIES.some(([c]) => c === country) && <option value={country}>{country || 'Choose…'}</option>}
              {COUNTRIES.map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </label>

          {!quote.available && (
            <p className="rounded-lg border border-(--line) bg-(--panel) p-3 text-sm">
              Paying on the website is not available for this country yet. Every reminder keeps working on the free plan.
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {quote.plans.map((p) => (
              <section key={p.productKey} className="flex flex-col gap-2 rounded-2xl border border-(--line) bg-(--panel) p-4">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-semibold">{TIER[p.tier] ?? p.tier}</h3>
                  <span className="text-lg font-bold">{p.price?.display ?? '—'}</span>
                </div>
                <span className="text-sm text-(--muted)">
                  {p.period === 'annual' ? 'every year' : 'every month'} · renews until you cancel
                </span>
                <button
                  type="button"
                  disabled={!quote.available || !p.ready || !p.price || busy !== null}
                  onClick={() => void choose(p.productKey)}
                  className="mt-1 inline-flex min-h-11 items-center justify-center rounded-xl bg-(--accent) px-4 font-semibold text-white disabled:opacity-40"
                >
                  {busy === p.productKey
                    ? 'Opening…'
                    : !quote.available || !p.ready || !p.price
                      ? 'Not available here yet'
                      : signedIn ? 'Choose this plan' : 'Sign in to choose'}
                </button>
              </section>
            ))}
          </div>

          <p className="text-xs leading-relaxed text-(--muted)">
            Prices are {quote.taxLine.charAt(0).toLowerCase() + quote.taxLine.slice(1)}{' '}
            Sold by <strong className="text-(--fg)">{quote.seller.name}</strong>, {quote.seller.address}.
            Paid {quote.gateway === 'razorpay' ? 'through Razorpay' : 'through Stripe'}. A plan renews automatically at the
            end of each period until you cancel; cancel any time and you keep it to the end of the period you paid for.
            By choosing a plan you agree to the{' '}
            <a className="underline" href={`${legalOrigin}/legal/terms`} target="_blank" rel="noreferrer">Terms of Use</a> and the{' '}
            <a className="underline" href={`${legalOrigin}/legal/refunds`} target="_blank" rel="noreferrer">Refunds and Cancellation</a> policy.
          </p>
        </>
      )}
    </div>
  );
}
